import { create } from "zustand";
import { persist } from "zustand/middleware";

type Field = HTMLInputElement | HTMLTextAreaElement;

/**
 * When to draw the built-in keyboard instead of using the platform's.
 *
 * - `auto` (default): only when the platform keyboard has just refused the
 *   remote's OK on an open field — the measured failure (`tvKeyboard.ts`). On a
 *   phone, tablet, desktop or any TV whose keyboard takes the D-pad, this never
 *   happens, so nothing changes there.
 * - `always`: raise it the moment a field is opened with a key press. The
 *   escape hatch for a device whose keyboard fails in a way the signal does not
 *   catch — a viewer can switch it on and get a keyboard that definitely works.
 * - `never`: off entirely.
 */
export type BuiltInKeyboard = "auto" | "always" | "never";

interface TvKeyboardState {
  mode: BuiltInKeyboard;
  setMode: (mode: BuiltInKeyboard) => void;
  /** Whether the built-in keyboard is on screen. */
  open: boolean;
  /** The field its keys write into. */
  target: Field | null;
  openFor: (field: Field) => void;
  close: () => void;
}

export const useTvKeyboard = create<TvKeyboardState>()(
  persist(
    (set, get) => ({
      mode: "auto",
      setMode: (mode) => {
        set({ mode });
        // Turning it off closes it: a viewer who just switched it off is not
        // about to type on the thing they switched off.
        if (mode === "never" && get().open) set({ open: false, target: null });
      },
      open: false,
      target: null,
      openFor: (target) => {
        if (get().mode === "never") return;
        set({ open: true, target });
      },
      close: () => set({ open: false, target: null }),
    }),
    {
      name: "slique-tv-keyboard",
      // Only the preference is stored. The target is a live DOM node and the
      // keyboard is never open across a reload.
      partialize: (state) => ({ mode: state.mode }),
    },
  ),
);
